import React, { useState, useEffect, useRef } from "react";
import Calendar from "react-calendar";
import '../../index.css';

const BookingCalendar = ({ apartmentId, onClose }) => {
  const [dailyPrices, setDailyPrices] = useState({});
  const [bookedDates, setBookedDates] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [priceInput, setPriceInput] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState({ symbol: "€", code: "EUR", id: 1 });
  const [currencyMap, setCurrencyMap] = useState({});
  const modalRef = useRef(null);

  const getApiBaseUrl = () => {
    return process.env.REACT_APP_API_URL || "http://localhost:8000";
  };

  // ✅ Utility to format date to YYYY-MM-DD
  const formatDate = (date) => {
    return date
      .toLocaleDateString("de-DE", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
      .split(".")
      .reverse()
      .join("-");
  };

  // ✅ Check if user is logged in and is owner
  const isOwner = () => {
    const token = localStorage.getItem("token");
    if (!token) return false;

    const rawGroups = localStorage.getItem("groups");
    let storedGroups = [];
    if (rawGroups) {
      try {
        const parsed = JSON.parse(rawGroups);
        storedGroups = Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        storedGroups = [rawGroups];
      }
    }
    return storedGroups.includes("Owners");
  };

  // ✅ Check if user is authenticated
  const isAuthenticated = () => {
    return !!localStorage.getItem("token");
  };

  // Fetch daily prices (for everyone)
  useEffect(() => {
    fetch(`${getApiBaseUrl()}/api/properties/apartments/${apartmentId}/daily-prices/`)
      .then((response) => response.json())
      .then((data) => {
        setDailyPrices(
          data.reduce((acc, item) => {
            acc[item.date] = { price: item.price, currency: item.currency };
            return acc;
          }, {})
        );
      })
      .catch((error) => console.error("Error fetching daily prices:", error));
  }, [apartmentId]);

  // ✅ Fetch booked dates for this apartment (use public endpoint unless logged in)
  useEffect(() => {
    const token = localStorage.getItem("token");

    // 👇 Choose correct endpoint
    const endpoint = isAuthenticated()
      ? `${getApiBaseUrl()}/api/bookings/?apartment=${apartmentId}`
      : `${getApiBaseUrl()}/api/public/bookings/?apartment=${apartmentId}`;

    const headers = isAuthenticated()
      ? { Authorization: `Token ${token}` }
      : {};

    fetch(endpoint, { headers })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        const bookings = Array.isArray(data) ? data : data.results || [];
        const booked = [];

        bookings.forEach((booking) => {
          const start = new Date(booking.start_date);
          const end = new Date(booking.end_date);
          const currentDate = new Date(start);

          // end_date is exclusive (checkout day) — do not mark it as blocked
          while (currentDate < end) {
            booked.push(formatDate(new Date(currentDate)));
            currentDate.setDate(currentDate.getDate() + 1);
          }
        });

        setBookedDates(booked);
      })
      .catch((error) => console.error("Error fetching bookings:", error));
  }, [apartmentId]);

  // Fetch currencies
  useEffect(() => {
    fetch(`${getApiBaseUrl()}/api/currencies/`)
      .then((res) => res.json())
      .then((data) => {
        setCurrencies(data);
        const euroCurrency = data.find((c) => c.code === "EUR");
        if (euroCurrency) setSelectedCurrency(euroCurrency);
        else if (data.length > 0) setSelectedCurrency(data[0]);

        const map = {};
        data.forEach((c) => {
          map[c.id] = c.symbol;
        });
        setCurrencyMap(map);
      })
      .catch((err) => console.error("Error fetching currencies:", err));
  }, []);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  // Show prices
  const tileContent = ({ date, view }) => {
    if (view === "month") {
      const formattedDate = formatDate(date);
      const daily = dailyPrices[formattedDate];

      if (daily && daily.price) {
        const symbol = currencyMap[daily.currency] || "";
        return (
          <div className="text-xs mt-1 font-semibold">
            {symbol}
            {daily.price}
          </div>
        );
      }
      return null;
    }
  };

  // Custom class for booked dates
  const tileClassName = ({ date, view }) => {
    if (view === "month") {
      const formattedDate = formatDate(date);
      if (bookedDates.includes(formattedDate)) {
        return "booked-day"; // red background in CSS
      }
    }
    return null;
  };

  // Disable booked dates
  const tileDisabled = ({ date, view }) => {
    if (view === "month") {
      const formattedDate = formatDate(date);
      return bookedDates.includes(formattedDate);
    }
    return false;
  };

  // Save price for each date in range (only owners)
  const savePriceForRange = async () => {
    const [startDate, endDate] = dateRange;
    if (!startDate || !endDate || priceInput === "") {
      setError("Please select a date range and enter a valid price.");
      return;
    }

    // Prevent overwriting booked dates
    const datesInRange = [];
    let currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const formattedDate = formatDate(currentDate);
      datesInRange.push(formattedDate);
      if (bookedDates.includes(formattedDate)) {
        setError("Cannot set prices for booked dates. Please select a different range.");
        return;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    setIsLoading(true);
    setError("");

    try {
      const updatedPrices = { ...dailyPrices };
      const updatePromises = datesInRange.map((date) => {
        updatedPrices[date] = { price: parseFloat(priceInput), currency: selectedCurrency.id };
        return fetch(`${getApiBaseUrl()}/api/properties/apartments/${apartmentId}/daily-prices/`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Token ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            date,
            price: parseFloat(priceInput),
            currency: selectedCurrency.id,
          }),
        });
      });

      const responses = await Promise.all(updatePromises);
      const allSuccessful = responses.every((response) => response.ok);

      if (!allSuccessful) {
        throw new Error("Some dates failed to update");
      }

      setDailyPrices(updatedPrices);
      alert("Prices updated successfully for the selected range!");
    } catch (error) {
      console.error("Error updating prices:", error);
      setError("Failed to update some prices. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div ref={modalRef} className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md relative custom-calendar">
        <button onClick={onClose} className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-xl">
          ✖
        </button>

        <h2 className="text-xl font-bold mb-4 text-center">Apartment Availability & Prices</h2>

        {/* Currency Dropdown - Only show for owners */}
        {isOwner() && (
          <div className="mb-4">
            <label className="mr-2 font-semibold">Currency:</label>
            <select
              value={selectedCurrency.id}
              onChange={(e) => {
                const currency = currencies.find((c) => c.id === Number(e.target.value));
                setSelectedCurrency(currency);
              }}
              className="border p-1 rounded text-sm"
            >
              {currencies.map((currency) => (
                <option key={currency.id} value={currency.id}>
                  {currency.name} ({currency.symbol})
                </option>
              ))}
            </select>
          </div>
        )}

        <Calendar
          onChange={isOwner() ? setDateRange : null}
          selectRange={isOwner()}
          tileContent={tileContent}
          tileClassName={tileClassName}
          tileDisabled={tileDisabled}
          className="w-full mb-4"
        />

        {/* Legend */}
        <div className="mt-4 flex items-center justify-center space-x-4 text-sm">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-[#ffcccc] mr-2 rounded-sm"></div>
            <span>Booked</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-white border border-gray-300 mr-2 rounded-sm"></div>
            <span>Available</span>
          </div>
        </div>

        {/* Price input - Only show for owners */}
        {isOwner() && dateRange[0] && dateRange[1] && (
          <div className="mt-4 p-4 bg-gray-50 rounded-md">
            <h3 className="font-semibold text-sm mb-2">
              Set Price for {formatDate(dateRange[0])} to {formatDate(dateRange[1])}
            </h3>
            <div className="flex space-x-2">
              <input
                type="number"
                value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)}
                placeholder={`Price (${selectedCurrency.symbol})`}
                className="border p-2 rounded text-sm flex-1"
              />
              <button
                onClick={savePriceForRange}
                disabled={isLoading}
                className="bg-blue-500 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 disabled:bg-blue-300 flex items-center"
              >
                {isLoading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        )}

        {/* Info for non-owners */}
        {!isOwner() && (
          <div className="mt-4 p-3 bg-blue-50 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>Red highlighted dates</strong> are already booked and unavailable.
            </p>
            <p className="text-sm text-blue-800 mt-1">
              Prices shown are per night. Contact us to book available dates!
            </p>
          </div>
        )}

        {error && (
          <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded">
            <p className="text-red-500 text-sm">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingCalendar;
